import { useEffect, useState } from 'react';
import { CAMERA_TRACKS, CAMERA_TRACK_LIMITS, evaluateAuthoredCameraTrack, resolveCameraKeyframes, type CameraKeyframe, type CameraTrack } from '../../domain/camera-keyframes';
import { automaticCameraAttributes } from '../../map/attribute-camera';
import { cameraLensMillimeters } from '../../domain/camera-keyframes';
import type { CameraPose } from '../../domain/camera-plan';
import type { DraftControls } from '../StudioSettings';
import type { CinematicEditing } from '../use-cinematic-waypoints';
import { cameraFrameSeconds, editableCameraTracks, upsertAttributeKey, upsertAttributeTiming, upsertOrientationKeys, type AttributeTiming } from './camera-animation';
import { platformRotation, travelHeadingWeight } from '../../../../renderer/src/camera-heading';
import { resolveRenderPreset } from '../../domain/render-preset';

export function useCameraAnimation({ draft, update }: DraftControls, editing: Pick<CinematicEditing, 'camera' | 'settings' | 'follow'> & Partial<Pick<CinematicEditing, 'sceneAt'>>, duration: number,
  pause: () => void, seek: (progress: number, persist?: boolean) => void, rendered?: import('../../map/attribute-camera').CameraAttributeValues | undefined,
  dimensions: { width: number; height: number } = resolveRenderPreset(draft.preset)) {
  const [selected, setSelected] = useState<{ axis: CameraTrack; id: string } | null>(null);
  const [error, setError] = useState('');
  const sceneSignature = JSON.stringify(draft.cinematicSequence.waypoints);
  useEffect(() => setSelected(null), [sceneSignature]);
  const tracks = draft.cinematicSequence.cameraTracks;
  const seconds = draft.progress * duration;
  const automaticAt = (time: number) => {
    const scene = editing.sceneAt?.(time);
    return automaticCameraAttributes(scene?.camera ?? editing.camera as CameraPose,
      cameraLensMillimeters(scene?.settings.cameraDistance ?? editing.settings.cameraDistance), dimensions.height, dimensions.width);
  };
  const base = automaticAt(seconds);
  const scene = editing.sceneAt?.(seconds);
  const headingWeight = scene ? travelHeadingWeight(scene) : editing.settings.cameraStyle === 'chase' ? 1 : 0;
  const headingLocked = headingWeight >= 1;
  const values = { ...base };
  const authored: Partial<typeof values> = {};
  if (draft.cinematicEnabled && tracks) for (const axis of CAMERA_TRACKS) {
    const value = evaluateAuthoredCameraTrack(tracks, axis, seconds, time => automaticAt(time)[axis]);
    if (value !== undefined) authored[axis] = value;
    values[axis] = value ?? base[axis];
  }
  values.rotation = platformRotation(authored.rotation, base.rotation, headingWeight) ?? base.rotation;
  if (headingLocked) delete authored.rotation;
  if (rendered) Object.assign(values, rendered);
  for (const axis of CAMERA_TRACKS) {
    if (authored[axis] !== undefined) values[axis] = axis === 'rotation'
      ? platformRotation(authored.rotation, base.rotation, headingWeight) ?? base.rotation
      : authored[axis]!;
  }
  const save = (change: () => NonNullable<typeof tracks>, progress?: number) => {
    try {
      const next = resolveCameraKeyframes(change(), duration);
      pause(); update(current => ({ ...current, ...(progress === undefined ? {} : { progress }), cinematicEnabled: true, cameraStyleVersion: 4,
        cinematicSequence: { ...current.cinematicSequence, cameraTracks: next } })); setError(''); return next;
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Camera edit could not be saved.'); return undefined; }
  };
  const select = (axis: CameraTrack, id: string) => {
    const key = tracks?.[axis]?.find((key, index) => (key.id ?? String(index)) === id); if (!key) return;
    pause(); editing.follow(); setSelected({ axis, id }); seek(key.seconds / duration, false);
  };
  const set = (axis: CameraTrack, value: number, automatic = false) => {
    if (axis === 'rotation' && headingLocked) return;
    const limits = CAMERA_TRACK_LIMITS[axis]; if (!Number.isFinite(value) || value < limits.min || value > limits.max) { setError(`Enter ${limits.min} through ${limits.max} ${limits.unit}.`); return; }
    try {
      const result = upsertAttributeKey(tracks, axis, seconds, value, base[axis], duration, automatic);
      if (save(() => result.tracks)) { editing.follow(); setSelected({ axis, id: result.key.id! }); }
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Camera edit could not be saved.'); }
  };
  const orient = (values: Partial<Record<'tilt' | 'roll', number>>) => {
    try {
      const result = upsertOrientationKeys(tracks, seconds, values, base, duration);
      if (save(() => result.tracks)) { editing.follow(); setSelected(result.selected ?? null); }
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Camera edit could not be saved.'); }
  };
  const timing = (axis: CameraTrack, change: AttributeTiming) => {
    try {
      const result = upsertAttributeTiming(tracks, axis, seconds, values[axis], base[axis], duration, change);
      if (save(() => result.tracks)) { editing.follow(); setSelected({ axis, id: result.key.id! }); }
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Camera timing could not be saved.'); }
  };
  const patch = (axis: CameraTrack, id: string, change: Partial<CameraKeyframe>) => {
    const index = tracks?.[axis]?.findIndex((key, index) => (key.id ?? String(index)) === id) ?? -1; if (index < 0) return;
    const seconds = change.seconds === undefined ? tracks![axis]![index]!.seconds : cameraFrameSeconds(change.seconds, duration);
    const next = save(() => {
      const current = editableCameraTracks(tracks), old = current[axis]![index]!;
      const key = { ...old, ...change, ...(change.value === undefined ? {} : { automatic: undefined }),
        ...(change.seconds === undefined ? {} : { seconds: cameraFrameSeconds(change.seconds, duration) }) };
      return { ...current, [axis]: current[axis]!.map((item, i) => i === index ? key : item) };
    }, seconds / duration);
    if (next) {
      const key = next[axis]?.find(item => item.id === (tracks?.[axis]?.[index]?.id ?? next[axis]?.[index]?.id));
      if (key) { editing.follow(); setSelected({ axis, id: key.id! }); seek(key.seconds / duration, false); }
    }
  };
  const remove = (axis: CameraTrack, id: string) => {
    save(() => { const current = editableCameraTracks(tracks); return { ...current, [axis]: (current[axis] ?? []).filter((_, index) => (tracks?.[axis]?.[index]?.id ?? String(index)) !== id) }; }); setSelected(null);
  };
  const key = selected && tracks?.[selected.axis]?.find((key, index) => (key.id ?? String(index)) === selected.id);
  useEffect(() => {
    if (key && Math.round(key.seconds / duration * (duration * 30 - 1)) !== Math.round(draft.progress * (duration * 30 - 1))) setSelected(null);
  }, [key, draft.progress, duration]);
  return { tracks, seconds, selected: key ? selected : null, key: key || undefined, values, authored, headingLocked, error, select, set, orient, timing, patch, remove,
    automatic: (axis: CameraTrack) => set(axis, Math.max(CAMERA_TRACK_LIMITS[axis].min, Math.min(CAMERA_TRACK_LIMITS[axis].max, base[axis])), true),
    clear: () => setSelected(null), targetLock: (targetLock: boolean) => save(() => ({ ...editableCameraTracks(tracks), targetLock })) };
}
export type CameraAnimation = ReturnType<typeof useCameraAnimation>;
